/**
 * DataList-specific conditional evaluation helpers.
 *
 * Wraps the shared `evaluateOperator` with DataList-specific logic
 * for row formatting, cell formatting, conditional icons, and
 * row action visibility.
 */

import { evaluateOperator } from '../../shared/conditionEvaluator';
import type {
  RowConditionalRule,
  CellConditionalRule,
  ConditionalIconRule,
  RowActionConfig,
} from '../types';

// ---------------------------------------------------------------------------
// Result Types
// ---------------------------------------------------------------------------

export interface RowStyles {
  className?: string;
  backgroundColor?: string;
  textColor?: string;
}

export interface CellStyles {
  className?: string;
  backgroundColor?: string;
  textColor?: string;
  fontWeight?: 'normal' | 'medium' | 'semibold' | 'bold';
}

export interface IconResult {
  icon: string;
  color?: string;
  position: 'before' | 'after' | 'replace';
}

// ---------------------------------------------------------------------------
// Internal: resolve the field value for a cell-level condition
// ---------------------------------------------------------------------------

/**
 * Resolves the value to evaluate for a cell-level condition.
 *
 * - When `field` is undefined, the cell's own `value` is used.
 * - When `field` starts with `"row."`, the corresponding row field is used.
 * - Otherwise, the field name is looked up directly on the row.
 */
function resolveCellFieldValue(
  value: unknown,
  row: Record<string, unknown>,
  field: string | undefined,
): unknown {
  if (field === undefined) {
    return value;
  }
  if (field.startsWith('row.')) {
    return row[field.slice(4)];
  }
  return row[field];
}

// ---------------------------------------------------------------------------
// evaluateRowRules
// ---------------------------------------------------------------------------

/**
 * Evaluates row-level conditional formatting rules against a row of data.
 * Multiple matching rules are merged (last wins for conflicting properties).
 */
export function evaluateRowRules(
  row: Record<string, unknown>,
  rules: RowConditionalRule[],
): RowStyles {
  const result: RowStyles = {};

  for (const rule of rules) {
    const fieldValue = row[rule.condition.field];
    const match = evaluateOperator(
      fieldValue,
      rule.condition.operator,
      rule.condition.value,
    );

    if (match) {
      if (rule.className !== undefined) {
        result.className = rule.className;
      }
      if (rule.backgroundColor !== undefined) {
        result.backgroundColor = rule.backgroundColor;
      }
      if (rule.textColor !== undefined) {
        result.textColor = rule.textColor;
      }
    }
  }

  return result;
}

// ---------------------------------------------------------------------------
// evaluateCellRules
// ---------------------------------------------------------------------------

/**
 * Evaluates cell-level conditional formatting rules.
 * When condition.field is provided, evaluates that field from the row.
 * When condition.field is omitted, evaluates the cell's own value.
 */
export function evaluateCellRules(
  value: unknown,
  row: Record<string, unknown>,
  rules: CellConditionalRule[],
): CellStyles {
  const result: CellStyles = {};

  for (const rule of rules) {
    const fieldValue = resolveCellFieldValue(
      value,
      row,
      rule.condition.field,
    );
    const match = evaluateOperator(
      fieldValue,
      rule.condition.operator,
      rule.condition.value,
    );

    if (match) {
      if (rule.className !== undefined) {
        result.className = rule.className;
      }
      if (rule.backgroundColor !== undefined) {
        result.backgroundColor = rule.backgroundColor;
      }
      if (rule.textColor !== undefined) {
        result.textColor = rule.textColor;
      }
      if (rule.fontWeight !== undefined) {
        result.fontWeight = rule.fontWeight;
      }
    }
  }

  return result;
}

// ---------------------------------------------------------------------------
// evaluateConditionalIcons
// ---------------------------------------------------------------------------

/**
 * Evaluates conditional icon rules for a cell.
 * Returns the first matching icon rule, or null if none match.
 */
export function evaluateConditionalIcons(
  value: unknown,
  row: Record<string, unknown>,
  rules: ConditionalIconRule[],
): IconResult | null {
  for (const rule of rules) {
    const fieldValue = resolveCellFieldValue(
      value,
      row,
      rule.condition.field,
    );
    const match = evaluateOperator(
      fieldValue,
      rule.condition.operator,
      rule.condition.value,
    );

    if (match) {
      return {
        icon: rule.icon,
        color: rule.color,
        position: rule.position ?? 'before',
      };
    }
  }

  return null;
}

// ---------------------------------------------------------------------------
// evaluateRowActionCondition
// ---------------------------------------------------------------------------

/**
 * Evaluates whether a row action's condition is met for a given row.
 * Returns true if no condition is defined (always show).
 */
export function evaluateRowActionCondition(
  row: Record<string, unknown>,
  condition: RowActionConfig['condition'],
): boolean {
  if (condition === undefined) {
    return true;
  }

  const fieldValue = row[condition.field];
  return evaluateOperator(fieldValue, condition.operator, condition.value);
}
